import { Dropdown, IconButton } from '@teo/components';
import {
  Information,
  Pencil,
  PlusFilled,
  Document,
  Chapter,
  Library,
  Play,
  Image,
  ImageFilled,
  PlayFilled,
  TaskCheckmarkFilled,
} from '@teo/components/icons';
import React, { useEffect, useContext, useState } from 'react';
import { LessonTreeEdit } from '@/components/LessonTree/LessonTreeEdit';
import { LibraryModal } from '../LibraryModal/LibraryModal';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { axios, publicAxios } from '@/axios';
import { config } from '@/config';
import { uploadLesson, uploadImage } from '@/query/documents';
import { newUid, uploadFile } from '@/util';
import { uploadVideo } from '@/query/vimeo';
import Hint from '../Hints/Hints';
import { XMLParser, XMLBuilder, XMLValidator } from 'fast-xml-parser';
import { HintsContext } from '@/components/Contexts/HintsContext';
import { PageUrlContext } from '@/components/Contexts/PageUrlContext';
import { NotificationContext } from '@/components/Contexts/NotificationContext';

export const SidebarScormStudent = ({
  coursecontentId,
  courseId,
  userId,
  publicationType,
  publicationPath,
  activeNode,
  autoInit,
  setActiveNode,
  isDesctop,
  setNumLesAs,
  notHidden,
  isMobile,
  setHideSidebar,
  refetchNonce,
}: any) => {
  const { t } = useTranslation();
  const [lessons, setLessons] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const { hints } = useContext(HintsContext);
  const { newPage } = useContext(PageUrlContext);
  const [isAddButtonBottom, setIsAddButtonBottom] = useState(false);
  const { setShowNotification } = useContext(NotificationContext);

  // ------------------------------------------------------------
  // Fetch lessonstatuses (for checkmarks)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!courseId || !userId) {
      setStatuses([]);
      return;
    }

    axios
      .get(`${config.backend}/lessonstatuses`, { params: { courseId, userId } })
      .then((res) => {
        const data = res?.data || [];
        setStatuses(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setStatuses([]);
      });
  }, [courseId, userId, refetchNonce]);

  const isDoneStatus = (s: any) =>
    ['completed', 'corrected'].includes(s?.status);

  // Match manifest item -> lessonstatus:
  // Prefer identifierref because BE stores lessonstatuses.version like "res-ch01"
  const isItemDone = (item: any) => {
    const key = item?.['@_identifierref'] || item?.['@_identifier'] || null;
    if (!key) return false;
    return (statuses || []).some(
      (s: any) => isDoneStatus(s) && s?.version === key
    );
  };

  // ------------------------------------------------------------
  // Fetch + parse imsmanifest.xml
  // ------------------------------------------------------------
  useEffect(() => {
    if (!coursecontentId || !publicationPath) {
      setLessons([]);
      setNumLesAs && setNumLesAs(0);
      return;
    }

    const url = `${config.s3root}scorm/${coursecontentId}/${publicationPath}/imsmanifest.xml`;

    publicAxios
      .get(url, { responseType: 'text' })
      .then((result) => {
        const raw = result?.data || '';

        const parser = new XMLParser({
          ignoreAttributes: false,
          isArray: (name: string) =>
            ['organization', 'resource', 'file', 'item'].includes(name),
        });

        let json: any;
        try {
          json = parser.parse(raw);
        } catch {
          setLessons([]);
          setNumLesAs && setNumLesAs(0);
          return;
        }

        const orgRaw = json?.manifest?.organizations?.organization;
        const org = Array.isArray(orgRaw) ? orgRaw[0] : orgRaw;
        const itemsRaw = org?.item || [];
        const items = Array.isArray(itemsRaw)
          ? itemsRaw
          : itemsRaw
          ? [itemsRaw]
          : [];

        setLessons(items);
        setNumLesAs && setNumLesAs(items.length);

        if (autoInit && items?.[0] && typeof setActiveNode === 'function') {
          setActiveNode(items[0]);
          if (items.length === 1) setHideSidebar(true);
        }
      })
      .catch(() => {
        setLessons([]);
        setNumLesAs && setNumLesAs(0);
      });
  }, [coursecontentId, publicationPath, autoInit, setActiveNode, setNumLesAs]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  const renderItem: any = (item: any, pathKey = 'root', indexLabel = '') => {
    if (!item) return null;

    if (Array.isArray(item)) {
      return (
        <>
          {item.map((x, idx) =>
            renderItem(
              x,
              `${pathKey}.${idx}`,
              indexLabel ? `${indexLabel}.${idx + 1}` : `${idx + 1}`
            )
          )}
        </>
      );
    }

    const done = isItemDone(item);

    // Active item detection (prefer identifier, fallback to identifierref)
    const activeIdentifier = activeNode?.['@_identifier'] || null;
    const activeIdentifierRef = activeNode?.['@_identifierref'] || null;
    const itemIdentifier = item?.['@_identifier'] || null;
    const itemIdentifierRef = item?.['@_identifierref'] || null;

    const isActive =
      (!!activeIdentifier && activeIdentifier === itemIdentifier) ||
      (!!activeIdentifierRef && activeIdentifierRef === itemIdentifierRef);

    return (
      <div key={pathKey}>
        <div
          onClick={() => {
            typeof setActiveNode === 'function' && setActiveNode(item);
            isMobile && setHideSidebar((prev: any) => !prev);
          }}
        >
          <div className="my-1 flex items-center justify-between">
            <div
              className="flex min-h-[40px] w-full items-center"
              style={{ paddingLeft: '0px' }}
            >
              <div
                className={clsx(
                  'flex w-full cursor-pointer items-center rounded-md p-1 sm:p-2',
                  'sm:hover:bg-grey-transparent-01',
                  isActive && 'bg-grey-transparent-01'
                )}
                data-testid="chapter"
              >
                {done ? (
                  <TaskCheckmarkFilled className="m-2 ml-1 mr-3 w-6 text-success-05 sm:ml-2 sm:mr-5" />
                ) : (
                  <div className="relative mr-3 ml-1 flex h-8 min-h-8 w-8 min-w-8 rounded-full border-2 border-[#1e1e2014] pl-0.5 text-center sm:ml-2 sm:mr-5 sm:h-9 sm:min-h-9 sm:w-9 sm:min-w-9">
                    <PlayFilled className="m-auto w-4 text-secondary-04" />
                  </div>
                )}

                <p className="w-full flex-1 pr-2 text-xs font-medium text-black sm:text-sm">
                  <span className="pr-2 sm:pr-4">{indexLabel}.</span>
                  {item.title}
                </p>
              </div>
            </div>
          </div>
        </div>

        {!!item.item && renderItem(item.item, `${pathKey}.item`, indexLabel)}
      </div>
    );
  };

  if (!lessons || lessons.length === 0) {
    return <div className="mt-3 flex flex-col" />;
  }

  return <>{renderItem(lessons, 'root', '')}</>;
};
